<div class="space-y-6">
  <!-- 1.1 Région administrative principale d'activité -->
  <div class="space-y-2">
    <div class="flex items-center justify-between">
      <span class="text-xs font-bold text-neutral-12 uppercase tracking-wider flex items-center gap-2">
        <span
          class="w-5 h-5 rounded-full bg-primary-2 text-primary-11 text-[11px] font-mono font-bold flex items-center justify-center"
          >1</span
        >
        Région administrative principale d'activité
      </span>
      <span class="text-[11px] text-neutral-9 font-medium"> Enregistrée sur votre profil agent </span>
    </div>

    <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
      <mat-select
        aria-label="Région administrative principale d'activité"
        placeholder="Sélectionner votre région principale"
        [value]="internalPrimaryRegion()"
        (valueChange)="setPrimaryRegion($event)"
      >
        @for (reg of availableRegions; track reg.code) {
          @let count = regionSelectionCounts()[reg.code] || 0;
          <mat-option [value]="reg.code">
            <div class="flex items-center justify-between w-full">
              <span class="font-medium">{{ reg.name }}</span>
              @if (count > 0) {
                <span
                  class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary-2 text-primary-11 ml-2"
                >
                  {{ count }} département{{ count > 1 ? 's' : '' }}
                </span>
              }
            </div>
          </mat-option>
        }
      </mat-select>
      <mat-icon matPrefix class="mr-3 text-neutral-8">public</mat-icon>
    </mat-form-field>
  </div>

  <!-- 1.2 Départements de la région -->
  <div class="space-y-3 pt-2">
    <div class="flex items-center justify-between flex-wrap gap-2">
      <span class="text-xs font-bold text-neutral-12 uppercase tracking-wider flex items-center gap-2">
        <span
          class="w-5 h-5 rounded-full bg-primary-2 text-primary-11 text-[11px] font-mono font-bold flex items-center justify-center"
          >2</span
        >
        Départements de la région
      </span>
      <div class="flex items-center gap-3 text-xs">
        <button
          type="button"
          class="text-primary-11 hover:text-primary-12 font-semibold hover:underline cursor-pointer flex items-center gap-1"
          (click)="selectAllRegionDepartments()"
        >
          <mat-icon class="w-3.5! h-3.5! text-sm!">done_all</mat-icon>
          Tout cocher pour cette région
        </button>
        <span class="text-neutral-5">•</span>
        <button
          type="button"
          class="text-neutral-9 hover:text-neutral-11 hover:underline font-medium cursor-pointer"
          (click)="deselectAllRegionDepartments()"
        >
          Tout décocher
        </button>
      </div>
    </div>

    <!-- Grille de chips des départements de la région sélectionnée -->
    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5 pt-1">
      @for (item of currentRegionDepartments(); track item.dept) {
        @let isSelected = selectedDepartments().includes(item.dept);
        <button
          type="button"
          class="flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-medium border transition-all text-left cursor-pointer"
          [class.bg-primary-9]="isSelected"
          [class.text-white]="isSelected"
          [class.border-primary-9]="isSelected"
          [class.shadow-sm]="isSelected"
          [class.bg-neutral-1]="!isSelected"
          [class.dark:bg-neutral-2]="!isSelected"
          [class.text-neutral-12]="!isSelected"
          [class.border-neutral-3]="!isSelected"
          [class.dark:border-neutral-4]="!isSelected"
          [class.hover:border-neutral-5]="!isSelected"
          [class.dark:hover:border-neutral-5]="!isSelected"
          [class.hover:bg-neutral-2]="!isSelected"
          [class.dark:hover:bg-neutral-3]="!isSelected"
          (click)="toggleDepartment(item.dept)"
        >
          <span class="truncate">
            <span
              class="font-bold mr-1.5 font-mono"
              [class.text-white!]="isSelected"
              [class.text-primary-11]="!isSelected"
            >
              {{ item.info.code }}
            </span>
            <span [class.text-white!]="isSelected">{{ item.info.name }}</span>
          </span>
          <mat-icon
            class="w-4! h-4! text-base! ml-1.5 shrink-0"
            [class.text-white!]="isSelected"
            [class.text-neutral-8]="!isSelected"
          >
            {{ isSelected ? 'check_circle' : 'add_circle_outline' }}
          </mat-icon>
        </button>
      }
    </div>
  </div>

  <!-- 1.3 Recherche rapide d'un département -->
  <div class="pt-5 border-t border-neutral-3 dark:border-neutral-4 space-y-2.5">
    <div class="space-y-1">
      <span class="text-xs font-bold text-neutral-12 uppercase tracking-wider flex items-center gap-2">
        <span
          class="w-5 h-5 rounded-full bg-primary-2 text-primary-11 text-[11px] font-mono font-bold flex items-center justify-center"
          >3</span
        >
        Ajouter un autre département (limitrophe ou hors région)
      </span>
      <p class="text-xs text-neutral-10 leading-relaxed">
        Vous intervenez à cheval sur deux départements ou régions ? Recherchez-les directement par nom ou
        numéro.
      </p>
    </div>

    <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
      <input
        aria-label="Rechercher un département par numéro ou nom"
        matInput
        placeholder="Rechercher par numéro ou nom (ex : 40, Landes, 32, Gers, 64...)"
        [value]="deptSearchQuery()"
        (input)="deptSearchQuery.set($any($event.target).value)"
      />
      <mat-icon matPrefix class="mr-3 text-neutral-8">search</mat-icon>
      @if (deptSearchQuery()) {
        <button
          mat-icon-button
          matSuffix
          type="button"
          class="text-neutral-9 mr-1"
          (click)="deptSearchQuery.set('')"
          title="Effacer la recherche"
        >
          <mat-icon class="w-4! h-4! text-base!">close</mat-icon>
        </button>
      }
    </mat-form-field>

    <!-- Suggestions immédiates sous l'input -->
    @if (deptSearchQuery().trim().length > 0) {
      <div
        class="p-4 bg-neutral-2/60 dark:bg-neutral-3/60 rounded-xl border border-neutral-3 dark:border-neutral-4 shadow-xs space-y-3 animate-in fade-in duration-200"
      >
        <div class="flex items-center justify-between">
          <span class="text-xs font-bold text-neutral-11 uppercase tracking-wider">
            Résultats pour « {{ deptSearchQuery() }} » :
          </span>
          <span class="text-[11px] text-neutral-9 italic">
            {{ deptSearchResults().length }} correspondance(s)
          </span>
        </div>

        @if (deptSearchResults().length > 0) {
          <div class="flex flex-wrap gap-2">
            @for (res of deptSearchResults(); track res.dept) {
              <button
                type="button"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all cursor-pointer"
                [class.bg-primary-9]="res.isSelected"
                [class.text-white!]="res.isSelected"
                [class.border-primary-9]="res.isSelected"
                [class.bg-white]="!res.isSelected"
                [class.dark:bg-neutral-2]="!res.isSelected"
                [class.text-neutral-12]="!res.isSelected"
                [class.border-neutral-3]="!res.isSelected"
                [class.dark:border-neutral-4]="!res.isSelected"
                (click)="toggleDepartment(res.dept)"
              >
                <span class="font-mono font-bold">{{ res.code }}</span>
                <span>{{ res.name }}</span>
                <span class="text-[10px] opacity-75">({{ res.regionName }})</span>
                <mat-icon class="w-3.5! h-3.5! text-sm! ml-0.5">
                  {{ res.isSelected ? 'check' : 'add' }}
                </mat-icon>
              </button>
            }
          </div>
        } @else {
          <p class="text-xs text-neutral-10 italic">Aucun département trouvé pour cette recherche.</p>
        }
      </div>
    }
  </div>

  <!-- 1.4 Récapitulatif global des départements sélectionnés -->
  @if (selectedDepartments().length > 0) {
    <div class="pt-5 border-t border-neutral-3 dark:border-neutral-4 space-y-3">
      <div class="flex items-center justify-between">
        <span class="text-xs font-bold text-neutral-12 uppercase tracking-wider">
          Total des secteurs couverts ({{ selectedDepartments().length }}) :
        </span>
        <span class="text-[11px] text-neutral-10 italic"> Cliquez sur la croix pour retirer un secteur </span>
      </div>

      <div class="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
        @for (dept of selectedDepartments(); track dept) {
          @let info = getDeptInfo(dept);
          <span
            class="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold bg-neutral-2/70 dark:bg-neutral-3/70 text-neutral-12 border border-neutral-3 dark:border-neutral-4 shadow-xs"
          >
            <span class="font-mono font-bold text-primary-11">{{ info.code }}</span>
            <span>{{ info.name }}</span>
            @if (info.regionName) {
              <span class="text-[10px] text-neutral-9 font-normal">({{ info.regionName }})</span>
            }
            <button
              type="button"
              class="hover:text-error-9 text-neutral-8 hover:bg-neutral-3 rounded-full p-0.5 transition-colors cursor-pointer flex items-center justify-center ml-0.5"
              (click)="removeDepartment(dept)"
              title="Supprimer ce département"
            >
              <mat-icon class="w-3.5! h-3.5! text-sm!">close</mat-icon>
            </button>
          </span>
        }
      </div>
    </div>
  } @else {
    <div
      class="p-4 bg-amber-2 dark:bg-amber-3/30 border border-amber-5 dark:border-amber-6 rounded-xl flex items-center gap-3"
    >
      <mat-icon class="text-amber-9 dark:text-amber-11 shrink-0 w-5! h-5! text-xl!">info</mat-icon>
      <p class="text-xs text-amber-11 font-medium">
        Veuillez sélectionner au moins un département pour pouvoir être mis en relation avec des vendeurs sur
        votre secteur.
      </p>
    </div>
  }
</div>
